import '../../common/EsCommon.css';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { API_SUBS_CHECKOUT, SESSION_ID_PARAM, VIEW_ACCOUNT, VIEW_ACCOUNT_SUBSCRIPTION, VIEW_SUBSCRIBE } from '../../common/EsConstants';
import { esDayMonYear } from '../../common/EsDate';
import { esRead } from '../../common/EsFetch';
import { esSubsRefresh } from '../../common/EsSubs';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';
import { EsLoading } from '../../common/EsLoading';

/**
 * Where Stripe sends the reader back. esback looks the session up and
 * fulfills it if the webhook has not, so a paid answer here is a paid sub.
 * The refresh puts the new subscription in front of every screen that reads it.
 * The same words serve a first year and a renewal, since either way the year was
 * added to the end; only a lifetime account, which owes nothing, is thanked for a
 * donation instead, and every line here calls its payment one (Tim, 2026-10-13). esback's
 * answer says which, so the page never waits on subs/me and never shows the wrong one first.
 *
 * A PAID SUBSCRIPTION GOES ON TO THE ACCOUNT HOME PAGE, BY A FULL LOAD (T41): the reader lands
 * on /view/account, and every screen, the nav bar first, is drawn afresh as a subscriber's
 * rather than patched one store at a time. A donation stays here for its thanks, and so does
 * an unpaid answer for its way back. go is the load, a prop because jsdom cannot watch one.
 */
export const esReloadTo = ( pPath ) => window.location.replace( pPath );

export default function EsSubscribeDone( { go = esReloadTo } ) {
    const t = useEsStrings();
    const [ params ] = useSearchParams();
    const aSessionId = params.get( SESSION_ID_PARAM );
    const [ answer, setAnswer ] = useState( null );
    const [ error, setError ] = useState( null );

    useEffect( () => {
        if ( !aSessionId ) return undefined;
        let aLive = true;

        esRead( API_SUBS_CHECKOUT + '/' + encodeURIComponent( aSessionId ) )
            .then( ( pAnswer ) => {
                if ( !aLive ) return;
                setAnswer( pAnswer );
                if ( pAnswer?.paid && !pAnswer.lifetime ) {
                    go( VIEW_ACCOUNT );
                    return;
                }
                esSubsRefresh();
            } )
            .catch( ( pError ) => { if ( aLive ) setError( pError.message ); } );

        return () => { aLive = false; };
    }, [ aSessionId, go ] );

    const aDonation = Boolean( answer?.lifetime );

    let aBody;
    if ( !aSessionId ) {
        aBody = <p data-testid="es-subscribe-done-missing">{ t( 'subscribe-done-missing', 'There is no payment to look up here.' ) }</p>;
    } else if ( error ) {
        aBody = <p className="text-danger" data-testid="es-subscribe-done-error">{ error }</p>;
    } else if ( !answer ) {
        aBody = <EsLoading data-testid="es-subscribe-done-loading">{ t( 'subscribe-done-checking', 'Checking your payment…' ) }</EsLoading>;
    } else if ( answer.paid ) {
        aBody = <>
            <p data-testid="es-subscribe-done-paid">
                { aDonation
                    ? t( 'subscribe-done-donated', 'Thank you for your donation.' )
                    : t( 'subscribe-done-paid', 'Thank you. Your subscription now runs until {date}.', { date: esDayMonYear( answer.subEnd ) } ) }
            </p>
            <p className="mt-4">
                <Link to={ VIEW_ACCOUNT_SUBSCRIPTION } data-testid="es-subscribe-done-account-link"
                      className="text-accent hover:underline">
                    { aDonation
                        ? t( 'subscribe-done-see-donations', 'See your donations' )
                        : t( 'subscribe-done-see-yours', 'See your subscription' ) }
                </Link>
            </p>
        </>;
    } else {
        aBody = <>
            <p data-testid="es-subscribe-done-unpaid">
                { answer.status === 'expired'
                    ? t( 'subscribe-done-expired', 'That checkout expired before it was paid. Nothing was charged.' )
                    : t( 'subscribe-done-pending', 'That payment has not gone through yet. Nothing was charged.' ) }
            </p>
            <p className="mt-4">
                <Link to={ VIEW_SUBSCRIBE } data-testid="es-subscribe-done-retry-link"
                      className="text-accent hover:underline">
                    { aDonation
                        ? t( 'subscribe-done-retry-donate', 'Back to Donate' )
                        : t( 'subscribe-done-retry', 'Back to Subscribe' ) }
                </Link>
            </p>
        </>;
    }

    return (
        <section data-testid="es-subscribe-done-page" className="relative z-10 pb-4 pt-nav-room">
            <EsMinis />
            <div className="container">
                <div className="mx-auto max-w-[640px] px-4">
                    <h1 className="mb-8 text-2xl font-bold text-heading sm:text-3xl" data-testid="es-subscribe-done-heading">
                        { aDonation ? t( 'subscribe-done-donated-heading', 'Donation' ) : t( 'subscribe-done-heading', 'Subscription' ) }
                    </h1>
                    { aBody }
                </div>
            </div>
        </section>
    );
}
